import { Lookup } from '../Interfaces/StipulationTypes';

/**
 * Column bound for StipulationCategory.Name (nvarchar(250)). Must match
 * StipulationCategory.NameMaxLength on the server, which truncates as a backstop.
 */
export const CUSTOM_STIP_NAME_MAX_LENGTH = 250;

export const normalizeStipName = (text: string | null | undefined): string => (text ?? '').trim();

/** Returns a validation message for a custom stip name, or undefined when it is acceptable. */
export const validateCustomStipName = (text: string | null | undefined): string | undefined => {
  const name = normalizeStipName(text);
  if (name.length === 0) {
    return 'Enter a stipulation name.';
  }
  if (name.length > CUSTOM_STIP_NAME_MAX_LENGTH) {
    return `Stipulation names are limited to ${CUSTOM_STIP_NAME_MAX_LENGTH} characters.`;
  }
  return undefined;
};

/** Existing categories whose name contains the typed text (case-insensitive). Empty text matches nothing. */
export const findMatchingCategories = (categories: Lookup[], text: string | null | undefined): Lookup[] => {
  const needle = normalizeStipName(text).toLowerCase();
  if (needle.length === 0) {
    return [];
  }
  return categories.filter(c => (c.name ?? '').toLowerCase().includes(needle));
};

/** The existing category whose name equals the typed text (trimmed, case-insensitive), if any. */
export const findExactDuplicate = (categories: Lookup[], text: string | null | undefined): Lookup | undefined => {
  const needle = normalizeStipName(text).toLowerCase();
  if (needle.length === 0) {
    return undefined;
  }
  return categories.find(c => normalizeStipName(c.name).toLowerCase() === needle);
};

/**
 * Pulls the server's `{ message }` out of an error thrown by Data/fetch.ts postData, which throws a
 * string like `Unable to post data: {"message":"..."}` for non-2xx JSON responses. Anything else
 * (network failure, an empty 403 body that fails JSON parsing) falls back to the given text.
 */
export const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  const text = typeof error === 'string' ? error : (error as { message?: unknown } | null)?.message;
  if (typeof text !== 'string') {
    return fallback;
  }
  const start = text.indexOf('{');
  if (start >= 0) {
    try {
      const parsed = JSON.parse(text.substring(start)) as { message?: unknown };
      if (typeof parsed?.message === 'string' && parsed.message.trim().length > 0) {
        return parsed.message;
      }
    } catch {
      // not JSON; fall through
    }
  }
  return fallback;
};
